import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../Context/TranslationContext';
import { Award, Target, Users, Medal, BookOpen, CheckSquare, Calendar, Clock, Star } from 'lucide-react';

const RajyapuraskarPage = () => {
  const { isTamil } = useTranslation();

  const translations = {
    title: {
      en: "Rajya Puraskar Scout/Guide Award",
      ta: "ராஜ்ய புரஸ்கார் சாரணர்/சாரணியர் விருது"
    },
    subtitle: {
      en: "State Level Recognition of Excellence",
      ta: "மாநில அளவிலான சிறப்பு அங்கீகாரம்"
    },
    description: {
      en: "The Rajya Puraskar is a prestigious state-level recognition awarded to Scouts and Guides who demonstrate exceptional skills, leadership, and dedication to the movement. It marks a significant milestone in a Scout's/Guide's journey towards the Rashtrapati Award.",
      ta: "ராஜ்ய புரஸ்கார் என்பது சிறந்த திறன்கள், தலைமைத்துவம் மற்றும் இயக்கத்திற்கான அர்ப்பணிப்பை வெளிப்படுத்தும் சாரணர்கள் மற்றும் சாரணியர்களுக்கு வழங்கப்படும் மதிப்புமிக்க மாநில அளவிலான அங்கீகாரமாகும். இது ராஷ்டிரபதி விருதை நோக்கிய சாரணர்/சாரணியரின் பயணத்தில் ஒரு முக்கிய மைல்கல்லைக் குறிக்கிறது."
    },
    sections: {
      eligibility: {
        title: {
          en: "Eligibility Requirements",
          ta: "தகுதித் தேவைகள்"
        },
        content: {
          en: [
            "Age between 13-17 years",
            "Completed Tritiya Sopan/Pratham Sopan",
            "Regular attendance in Unit meetings",
            "Active participation in Unit activities",
            "Good academic performance",
            "Exemplary character and conduct"
          ],
          ta: [
            "வயது 13-17 ஆண்டுகளுக்கு இடையில்",
            "திரிதிய சோபான்/பிரதம் சோபான் நிறைவு செய்திருக்க வேண்டும்",
            "அலகு கூட்டங்களில் வழக்கமான வருகை",
            "அலகு செயல்பாடுகளில் செயலில் பங்கேற்பு",
            "நல்ல கல்வி செயல்திறன்",
            "முன்மாதிரியான குணம் மற்றும் நடத்தை"
          ]
        }
      },
      requirements: {
        title: {
          en: "Award Requirements",
          ta: "விருது தேவைகள்"
        },
        proficiencyBadges: {
          title: {
            en: "Required Proficiency Badges",
            ta: "தேவையான தேர்ச்சி பேட்ஜ்கள்"
          },
          badges: {
            en: [
              "First Aid",
              "Conservation",
              "Pioneering",
              "Camping",
              "Civil Defense",
              "Community Service",
              "Physical Fitness",
              "Culture & Heritage"
            ],
            ta: [
              "முதலுதவி",
              "பாதுகாப்பு",
              "முன்னோடி",
              "முகாமிடல்",
              "சிவில் பாதுகாப்பு",
              "சமூக சேவை",
              "உடல் தகுதி",
              "கலாச்சாரம் & பாரம்பரியம்"
            ]
          }
        },
        serviceProject: {
          title: {
            en: "Service Project Requirements",
            ta: "சேவை திட்டத் தேவைகள்"
          },
          content: {
            en: [
              {
                title: "Community Service",
                details: "Regular participation in community service activities",
                hours: "Minimum 50 hours"
              },
              {
                title: "Conservation Project",
                details: "Environmental or heritage conservation project",
                hours: "Minimum 30 hours"
              },
              {
                title: "School/Unit Service",
                details: "Service to school or scout/guide unit",
                hours: "Minimum 20 hours"
              }
            ],
            ta: [
              {
                title: "சமூக சேவை",
                details: "சமூக சேவை நடவடிக்கைகளில் வழக்கமான பங்கேற்பு",
                hours: "குறைந்தபட்சம் 50 மணிநேரம்"
              },
              {
                title: "பாதுகாப்பு திட்டம்",
                details: "சுற்றுச்சூழல் அல்லது பாரம்பரிய பாதுகாப்பு திட்டம்",
                hours: "குறைந்தபட்சம் 30 மணிநேரம்"
              },
              {
                title: "பள்ளி/அலகு சேவை",
                details: "பள்ளி அல்லது சாரணர்/சாரணியர் அலகுக்கான சேவை",
                hours: "குறைந்தபட்சம் 20 மணிநேரம்"
              }
            ]
          }
        },
        skills: {
          title: {
            en: "Required Skills",
            ta: "தேவையான திறன்கள்"
          },
          categories: {
            en: [
              {
                category: "Scouting Skills",
                items: [
                  "Advanced knots and lashings",
                  "Map reading and navigation",
                  "Campcraft and outdoor cooking",
                  "First aid and emergency response"
                ]
              },
              {
                category: "Leadership Skills",
                items: [
                  "Team management",
                  "Event planning",
                  "Communication",
                  "Problem-solving"
                ]
              },
              {
                category: "Community Skills",
                items: [
                  "Project planning",
                  "Public speaking",
                  "Cultural awareness",
                  "Environmental conservation"
                ]
              }
            ],
            ta: [
              {
                category: "சாரணர் திறன்கள்",
                items: [
                  "மேம்பட்ட முடிச்சுகள் மற்றும் கட்டுதல்",
                  "வரைபட வாசிப்பு மற்றும் வழிசெலுத்தல்",
                  "முகாம் கலை மற்றும் வெளிப்புற சமையல்",
                  "முதலுதவி மற்றும் அவசர பதில்"
                ]
              },
              {
                category: "தலைமைத்துவ திறன்கள்",
                items: [
                  "குழு மேலாண்மை",
                  "நிகழ்வு திட்டமிடல்",
                  "தொடர்பாடல்",
                  "சிக்கல் தீர்த்தல்"
                ]
              },
              {
                category: "சமூக திறன்கள்",
                items: [
                  "திட்ட திட்டமிடல்",
                  "பொது பேச்சு",
                  "கலாச்சார விழிப்புணர்வு",
                  "சுற்றுச்சூழல் பாதுகாப்பு"
                ]
              }
            ]
          }
        }
      },
      testing: {
        title: {
          en: "Testing Process",
          ta: "சோதனை செயல்முறை"
        },
        stages: {
          en: [
            {
              stage: "Written Test",
              details: "Assessment of scouting knowledge and theory"
            },
            {
              stage: "Practical Examination",
              details: "Demonstration of scouting skills and techniques"
            },
            {
              stage: "Project Presentation",
              details: "Presentation of service projects and activities"
            },
            {
              stage: "District Level Review",
              details: "Evaluation by district level officials"
            },
            {
              stage: "State Level Approval",
              details: "Final review and approval by state authorities"
            }
          ],
          ta: [
            {
              stage: "எழுத்துத் தேர்வு",
              details: "சாரணர் அறிவு மற்றும் கோட்பாடு மதிப்பீடு"
            },
            {
              stage: "செயல்முறை தேர்வு",
              details: "சாரணர் திறன்கள் மற்றும் நுட்பங்களின் செயல்விளக்கம்"
            },
            {
              stage: "திட்ட விளக்கக்காட்சி",
              details: "சேவை திட்டங்கள் மற்றும் செயல்பாடுகளின் விளக்கக்காட்சி"
            },
            {
              stage: "மாவட்ட அளவிலான மதிப்பாய்வு",
              details: "மாவட்ட அளவிலான அதிகாரிகளால் மதிப்பீடு"
            },
            {
              stage: "மாநில அளவிலான ஒப்புதல்",
              details: "மாநில அதிகாரிகளால் இறுதி மதிப்பாய்வு மற்றும் ஒப்புதல்"
            }
          ]
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 mt-16">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block p-2 px-4 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              {isTamil ? 'மாநில அளவிலான விருது' : 'State Level Award'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {translations.title[isTamil ? 'ta' : 'en']}
            </h1>
            <h2 className="text-xl md:text-2xl text-blue-600 font-semibold mb-6">
              {translations.subtitle[isTamil ? 'ta' : 'en']}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {translations.description[isTamil ? 'ta' : 'en']}
            </p>
          </div>

          {/* Award Image */}
          <div className="mb-16 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="/Images/rajya-puraskar-badge.png"
              alt="Rajya Puraskar Award"
              className="w-205 max-w-md mx-auto"
            />
          </div>

          {/* Eligibility Section */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Target className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.eligibility.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {translations.sections.eligibility.content[isTamil ? 'ta' : 'en'].map((item, index) => (
                <div key={index} className="flex items-start">
                  <CheckSquare className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Proficiency Badges Section */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Medal className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {translations.sections.requirements.proficiencyBadges.badges[isTamil ? 'ta' : 'en'].map((badge, index) => (
                <div key={index} className="flex items-center bg-gray-50 p-4 rounded-lg">
                  <Star className="w-5 h-5 text-yellow-500 mr-3" />
                  <span className="text-gray-700">{badge}</span>
                </div>
              ))}
            </div>
          </div>
{/* Service Requirements */}
<div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Users className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.serviceProject.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {translations.sections.requirements.serviceProject.content[isTamil ? 'ta' : 'en'].map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-700 mb-4">{service.details}</p>
                  <div className="flex items-center text-blue-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span className="font-medium">{service.hours}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Required Skills */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <BookOpen className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.skills.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {translations.sections.requirements.skills.categories[isTamil ? 'ta' : 'en'].map((category, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.category}</h3>
                  <ul className="space-y-3">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start">
                        <CheckSquare className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Testing Process */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-8">
              <Calendar className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.testing.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {translations.sections.testing.stages[isTamil ? 'ta' : 'en'].map((stage, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{stage.stage}</h3>
                  </div>
                  <p className="text-gray-700">{stage.details}</p>
                </div>
              ))}
            </div>
          </div>

   
        </motion.div>
      </div>
    </div>
  );
};

export default RajyapuraskarPage;